import { zodResolver } from "@hookform/resolvers/zod";
import {
  Controller,
  FormProvider,
  type SubmitHandler,
  useForm,
  useFormContext,
} from "react-hook-form";
import { useListData } from "react-stately";
import type { z } from "zod";

import * as schemas from "@blis/shared/schemas/user";
import { trpc } from "@blis/trpc/react";

import { ConfirmPrompt } from "@/components/ConfirmPrompt";
import { Link } from "@/components/Link";
import { Button } from "@/components/ui/button";
import { FieldError, Input, Label, TextField } from "@/components/ui/field";
import { MultipleSelect } from "@/components/ui/multiple-select";
import {
  Select,
  SelectItem,
  SelectList,
  SelectTrigger,
} from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { Text } from "@/components/ui/text";
import { format } from "@/lib/utils";
import { mutationResponse } from "@/utils/trpc";
import type { UserDetail } from "../../types";
import { AdminForm } from "../AdminForm";
import { TabLayout } from "../TabLayout";

type ClientOption = { id: number; name: string };

const roles: { value: string; label: string }[] = [
  {
    label: "Org readonly",
    value: "blis_org_readonly",
  },
  {
    label: "Org tech (Field tech)",
    value: "blis_org_tech",
  },
  {
    label: "Read only",
    value: "blis_lab_readonly",
  },
  {
    label: "Lab assistant",
    value: "blis_lab_assistant",
  },
  {
    label: "Lab Tech",
    value: "blis_lab_tech",
  },
  {
    label: "Lab Manager",
    value: "blis_lab_manager",
  },
  {
    label: "Admin",
    value: "blis_lab_admin",
  },
];

const schema = schemas.updateOverride;
type FormValues = z.infer<typeof schema>;

export function UserDetailForm({ user }: { user: UserDetail }) {
  const { data } = trpc.orgs.list.useQuery();

  const orgs = data || [];

  const methods = useForm({
    defaultValues: {
      ...user,
      role: user?.defaultRole || "",
      allowedOrgs: (user?.allowedOrgs as number[]) || [],
    },
    resolver: zodResolver(schema),
  });

  const update = trpc.admin.user.updateOverride.useMutation();
  const activate = trpc.admin.user.activate.useMutation();
  const deactivate = trpc.admin.user.deactivate.useMutation();

  const onSubmit: SubmitHandler<FormValues> = (values) => {
    if (user) {
      update.mutate(
        values,
        mutationResponse({
          errorTitle: "Error updating user",
          successMessage: "User updated",
        }),
      );
    }
  };

  const handleActivate = () => {
    activate.mutate(
      { id: user.id },
      mutationResponse({
        errorTitle: "Error activating user",
        successMessage: "User activated successfully",
      }),
    );
  };

  const handleDeactivate = () => {
    deactivate.mutate(
      { id: user.id },
      mutationResponse({
        errorTitle: "Error deactivating user",
        successMessage: "User deactivated successfully",
      }),
    );
  };

  return (
    <TabLayout>
      <AdminForm onSubmit={methods.handleSubmit(onSubmit)}>
        <div>
          <Label>Last login</Label>
          <Text elementType="p" mono>
            {format(user.lastLogin) || "-"}
          </Text>
        </div>
        <div>
          <Label>Account Status</Label>
          <Text
            elementType="p"
            className={user.isActive ? "text-green-600" : "text-red-600"}
          >
            {user.isActive ? "Active" : "Deactivated"}
          </Text>
        </div>
        <FormProvider {...methods}>
          <div className="flex flex-col gap-2">
            <TextField value={user?.email || ""} id="user-email" isReadOnly>
              <Label>Email</Label>
              <Input />
            </TextField>
            <TextField
              value={user?.username || ""}
              id="user-username"
              isReadOnly
            >
              <Label>Username</Label>
              <Input />
            </TextField>

            <Controller
              control={methods.control}
              name="role"
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Select
                  {...field}
                  selectedKey={field.value}
                  onSelectionChange={field.onChange}
                  isInvalid={!!error}
                  validationBehavior="aria"
                >
                  <Label>Role</Label>
                  <SelectTrigger />
                  <SelectList items={roles}>
                    {(item) => (
                      <SelectItem id={item.value}>{item.label}</SelectItem>
                    )}
                  </SelectList>
                  <FieldError>{error?.message}</FieldError>
                </Select>
              )}
            />
            <Controller
              control={methods.control}
              name="orgId"
              render={({ field: { ref, ...field }, fieldState: { error } }) => (
                <Select
                  {...field}
                  selectedKey={field.value}
                  onSelectionChange={field.onChange}
                  isInvalid={!!error}
                  validationBehavior="aria"
                >
                  <Label>Client</Label>
                  <SelectTrigger />
                  <SelectList items={orgs}>
                    {(item) => (
                      <SelectItem id={item.id}>{item.name}</SelectItem>
                    )}
                  </SelectList>
                  <FieldError>{error?.message}</FieldError>
                </Select>
              )}
            />
            {orgs.length > 0 && (
              <AdditionalClientSelect orgs={orgs} user={user} />
            )}
          </div>
          <Separator />
          <div className="flex justify-between items-center">
            <div className="flex gap-2">
              {user.isActive ? (
                <ConfirmPrompt
                  title="Deactivate User"
                  message={`Are you sure you want to deactivate ${user.username}? They will no longer be able to sign in.`}
                  onConfirm={handleDeactivate}
                  loading={deactivate.isPending}
                  danger
                >
                  <Button variant="destructive" type="button">
                    Deactivate User
                  </Button>
                </ConfirmPrompt>
              ) : (
                <ConfirmPrompt
                  title="Activate User"
                  message={`Are you sure you want to activate ${user.username}? They will be able to sign in again.`}
                  onConfirm={handleActivate}
                  loading={activate.isPending}
                >
                  <Button variant="default" type="button">
                    Activate User
                  </Button>
                </ConfirmPrompt>
              )}
            </div>
            <div className="flex gap-2">
              <Link to="/admin/users" variant="outline" asButton>
                Cancel
              </Link>
              <Button type="submit" isPending={update.isPending}>
                Save
              </Button>
            </div>
          </div>
        </FormProvider>
      </AdminForm>
    </TabLayout>
  );
}

function AdditionalClientSelect({
  orgs,
  user,
}: {
  orgs: ClientOption[];
  user: UserDetail;
}) {
  const { control } = useFormContext<FormValues>();

  const selectedItems = useListData<ClientOption>({
    initialItems: orgs.filter((o) =>
      (user.allowedOrgs as number[]).includes(o.id),
    ),
  });

  return (
    <Controller
      control={control}
      name="allowedOrgs"
      render={({ field, fieldState: { error } }) => (
        <>
          {orgs.length > 0 && (
            <MultipleSelect
              label="Additional clients"
              menuTrigger="focus"
              description="This user can create and access accessions for these clients."
              onItemInserted={(item) => {
                const existing = field.value;
                const org = orgs.find((o) => o.id === item);
                if (org) {
                  field.onChange([...existing, item]);
                }
              }}
              onItemCleared={(item) => {
                const existing = field.value;
                field.onChange(existing.filter((o) => o !== item));
                selectedItems.remove(item);
              }}
              items={orgs.filter((o) => o.id !== user?.orgId)}
              selectedItems={selectedItems}
              tag={(item) => (
                <MultipleSelect.Tag id={item.id} textValue={item.name}>
                  {item.name}
                </MultipleSelect.Tag>
              )}
              errorMessage={error?.message}
            >
              {(item) => (
                <MultipleSelect.Option id={item.id} textValue={item.name}>
                  {item.name}
                </MultipleSelect.Option>
              )}
            </MultipleSelect>
          )}
        </>
      )}
    />
  );
}
